import { useClient } from '@cortex/react-query';
import type {
  CreateWorkflowScheduleInput,
  IScheduleTagPair,
  UpdateWorkflowScheduleInput,
  WorkflowSchedule,
} from '@cortex/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type CSSProperties } from 'react';

import { getBrowserTimezone, timezones } from '../../lib/cron-presets';
import {
  cronPresetsFor,
  describeScheduleActionError,
  managedMinCadenceMinutes,
  managedScheduleLockedTitle,
  useScheduleTagKeySuggestions,
  workflowSchedulesKey,
} from '../../lib/workflow-schedules';
import { Modal } from '../common/Modal';

import { ScheduleTagsEditor, tagRowsToTags, tagsToTagRows } from './ScheduleTags';

const textareaStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 'var(--fs-xs)',
  minHeight: 96,
};

/**
 * Create / edit a workflow schedule (DEV-17470): name, cron with the same
 * presets the refinement Schedules page uses, IANA timezone, optional JSON
 * trigger input, and the overlap guard. POSTs to
 * `v1/workflows/:id/schedules` (or PATCHes when `existing` is set) and
 * invalidates the schedule list on success.
 *
 * On a Formant-managed workflow (DEV-17471) only the cron and the tags are
 * the person's: the name, input, zone and overlap guard are the template's
 * (the API answers 409 for them), the presets keep to the product floor, and
 * the PATCH carries the cron and the tags alone.
 *
 * Tags (DEV-17502): key / value rows, keys suggested from the org's existing
 * schedule tags, sent as `tags` on create and update (`{}` clears).
 */
export function WorkflowScheduleModal({
  open,
  workflowId,
  existing,
  managed = false,
  onClose,
  onSaved,
}: {
  open: boolean;
  workflowId: string;
  /** Set to edit; null to create. */
  existing: WorkflowSchedule | null;
  /** The workflow is Formant-managed: cadence only. */
  managed?: boolean;
  onClose: () => void;
  onSaved: (schedule: WorkflowSchedule) => void;
}) {
  const client = useClient();
  const qc = useQueryClient();

  const [name, setName] = useState('');
  const [cron, setCron] = useState('0 9 * * *');
  const [tz, setTz] = useState(getBrowserTimezone());
  const [inputText, setInputText] = useState('{}');
  const [preventOverlap, setPreventOverlap] = useState(true);
  const [tagRows, setTagRows] = useState<IScheduleTagPair[]>([]);
  const [inputError, setInputError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const presets = cronPresetsFor(managed);
  const tagKeySuggestions = useScheduleTagKeySuggestions(open);

  // Re-seed when the dialog opens or the edit target changes.
  useEffect(() => {
    if (!open) return;
    if (existing) {
      setName(existing.name);
      setCron(existing.cron);
      setTz(existing.timezone);
      setInputText(JSON.stringify(existing.input ?? {}, null, 2));
      setPreventOverlap(existing.preventOverlap);
      setTagRows(tagsToTagRows(existing.tags));
    } else {
      setName('');
      setCron('0 9 * * *');
      setTz(getBrowserTimezone());
      setInputText('{}');
      setPreventOverlap(true);
      setTagRows([]);
    }
    setInputError(null);
    setSaveError(null);
  }, [open, existing]);

  const save = useMutation({
    mutationFn: (input: Record<string, unknown>) => {
      const tags = tagRowsToTags(tagRows);
      if (existing) {
        // A managed schedule's locked fields answer 409 when sent: only the
        // cadence and the tags travel.
        const body: UpdateWorkflowScheduleInput = managed
          ? { cron: cron.trim(), tags }
          : {
              name: name.trim(),
              cron: cron.trim(),
              timezone: tz,
              input,
              tags,
              preventOverlap,
            };
        return client.http
          .patch(`v1/workflows/${workflowId}/schedules/${existing.id}`, { json: body })
          .json<WorkflowSchedule>();
      }
      const body: CreateWorkflowScheduleInput = {
        name: name.trim(),
        cron: cron.trim(),
        timezone: tz,
        input,
        tags,
        preventOverlap,
      };
      return client.http
        .post(`v1/workflows/${workflowId}/schedules`, { json: body })
        .json<WorkflowSchedule>();
    },
    onSuccess: async (schedule) => {
      try {
        await qc.invalidateQueries({ queryKey: workflowSchedulesKey(workflowId) });
      } catch (err) {
        console.error(err);
      }
      onSaved(schedule);
    },
    onError: async (err) => setSaveError(await describeScheduleActionError(err)),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !cron.trim()) return;
    const parsed = parseInputObject(inputText);
    if ('error' in parsed) {
      setInputError(parsed.error);
      return;
    }
    setInputError(null);
    setSaveError(null);
    save.mutate(parsed.value);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      eyebrow={existing ? 'EDIT SCHEDULE' : 'SCHEDULE WORKFLOW'}
      title={existing ? existing.name : 'New schedule'}
      width="min(680px, 92vw)"
    >
      <form
        onSubmit={handleSubmit}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}
      >
        <label className="field">
          <span className="field-label">Name</span>
          <input
            type="text"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required={true}
            placeholder="Morning fleet brief"
            aria-label="Schedule name"
            disabled={managed}
            title={managed ? managedScheduleLockedTitle : undefined}
          />
        </label>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 'var(--sp-3)',
          }}
        >
          {/* Plain <div>, not <label>: the preset chips are <button>s and a
              button inside a label that wraps an input gets its click
              swallowed by the label's redirect-to-input behaviour. */}
          <div className="field">
            <label className="field-label" htmlFor="workflow-schedule-cron">
              Cron expression
            </label>
            <input
              id="workflow-schedule-cron"
              type="text"
              className="input"
              value={cron}
              onChange={(e) => setCron(e.target.value)}
              required={true}
              placeholder="min hour dom mon dow"
            />
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--sp-1)',
                marginTop: 'var(--sp-1)',
              }}
            >
              {presets.map((p) => (
                <button
                  key={p.expr}
                  type="button"
                  className={`btn btn--xs ${cron === p.expr ? 'btn--primary' : 'btn--outline'}`}
                  onClick={() => setCron(p.expr)}
                  title={p.expr}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <span className="field-help">
              5-field cron (e.g. <code>0 9 * * *</code> = daily 09:00). Shortest cadence:{' '}
              {managed ? `${managedMinCadenceMinutes} minutes` : '1 minute'}.
            </span>
          </div>
          <label className="field">
            <span className="field-label">Timezone</span>
            {managed ? (
              // The zone is the module's: shown, not editable.
              <input
                className="input"
                value={tz}
                readOnly={true}
                disabled={true}
                title={managedScheduleLockedTitle}
                aria-label="Timezone"
              />
            ) : (
              <select
                className="select-native"
                value={tz}
                onChange={(e) => setTz(e.target.value)}
                aria-label="Timezone"
              >
                {timezones.map((z) => (
                  <option key={z} value={z}>
                    {z}
                  </option>
                ))}
              </select>
            )}
            <span className="field-help">
              {managed
                ? 'Set by the view this schedule belongs to.'
                : 'IANA name. Defaults to your browser’s timezone.'}
            </span>
          </label>
        </div>

        <label className="field">
          <span className="field-label">Trigger input (JSON, optional)</span>
          <textarea
            className="textarea"
            style={textareaStyle}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            rows={4}
            spellCheck={false}
            aria-label="Schedule input JSON"
            readOnly={managed}
            disabled={managed}
            title={managed ? managedScheduleLockedTitle : undefined}
          />
          <span className="field-help">
            Sent as the run input every fire. Validated against the workflow&apos;s input schema
            when the run starts.
          </span>
          {inputError ? <span className="field-error">{inputError}</span> : null}
        </label>

        <ScheduleTagsEditor rows={tagRows} onChange={setTagRows} suggestions={tagKeySuggestions} />

        <label
          className="field"
          style={{ flexDirection: 'row', alignItems: 'center', gap: 'var(--sp-2)' }}
        >
          <input
            type="checkbox"
            checked={preventOverlap}
            onChange={(e) => setPreventOverlap(e.target.checked)}
            disabled={managed}
            title={managed ? managedScheduleLockedTitle : undefined}
          />
          <span>Skip a slot while the previous run is still in progress</span>
        </label>

        {saveError ? <div className="field-error">{saveError}</div> : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary btn--sm" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : existing ? 'Save' : 'Create schedule'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** Empty text means `{}`; anything else must parse to a JSON object. */
export function parseInputObject(
  raw: string,
): { value: Record<string, unknown> } | { error: string } {
  const trimmed = raw.trim();
  if (!trimmed) return { value: {} };
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch (err) {
    return { error: `Input is not valid JSON: ${(err as Error).message}` };
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { error: 'Input must be a JSON object (e.g. {"scope": "fleet"})' };
  }
  return { value: parsed as Record<string, unknown> };
}
